import React, { createContext, useContext, useRef } from 'react';

/**
 * Lightweight SSR-compatible head tag collector.
 *
 * On the client side SEOHead manipulates `document.head` directly via useEffect
 * (same as the original implementation). On the server side a HeadCollector
 * instance is provided via context so that SEOHead can push tags into it
 * during renderToString, and the prerender script can read them out afterward.
 */

export interface HeadTag {
  tag: 'title' | 'meta' | 'link' | 'script';
  attributes?: Record<string, string>;
  children?: string;
}

export class HeadCollector {
  tags: HeadTag[] = [];

  push(tag: HeadTag) {
    this.tags.push(tag);
  }

  /** Serialise collected tags to an HTML string for injection into the shell. */
  toHTML(): string {
    return this.tags
      .map((t) => {
        const attrs = t.attributes
          ? ' ' +
            Object.entries(t.attributes)
              .map(([k, v]) => `${k}="${escapeAttr(v)}"`)
              .join(' ')
          : '';
        if (t.tag === 'title') {
          return `<title data-ssg="true">${escapeHtml(t.children ?? '')}</title>`;
        }
        if (t.tag === 'script') {
          return `<script${attrs}>${t.children ?? ''}</script>`;
        }
        // Self-closing tags (meta, link)
        return `<${t.tag}${attrs} data-ssg="true" />`;
      })
      .join('\n    ');
  }
}

const HeadContext = createContext<HeadCollector | null>(null);

export function useHeadCollector(): HeadCollector | null {
  return useContext(HeadContext);
}

/**
 * Wrap the app tree with this provider only during SSR pre-rendering.
 * On the client side, no provider is needed — SEOHead falls back to
 * direct DOM manipulation.
 */
export const HeadProvider: React.FC<{
  collector: HeadCollector;
  children: React.ReactNode;
}> = ({ collector, children }) => {
  return <HeadContext.Provider value={collector}>{children}</HeadContext.Provider>;
};

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
